import Qty from 'js-quantities';

// Types
export type UnitsSystem = 'imperial' | 'metric';
export type RawValue = string | number | null;

export interface UnitsOption {
  readonly value: UnitsSystem;
  readonly label: string;
}

// Constants
export const UNITS_OPTIONS: readonly UnitsOption[] = [
  { value: 'imperial', label: 'Imperial' },
  { value: 'metric', label: 'Metric' },
] as const;

export const WEIGHT_UNITS: Record<UnitsSystem, string> = {
  imperial: 'lbs',
  metric: 'kg',
} as const;

export const HEIGHT_UNITS: Record<UnitsSystem, string> = {
  imperial: 'in',
  metric: 'cm',
} as const;

// Validation utilities
const isValidValue = (value: RawValue): value is string | number => {
  return value !== null && value !== undefined && value !== '';
};

const isValidUnitsSystem = (units: string): units is UnitsSystem => {
  return units === 'imperial' || units === 'metric';
};

// Height conversion utilities
export const feetInchesToInches = (maskString: string): string => {
  if (!maskString || typeof maskString !== 'string') {
    return '0';
  }

  const parts = maskString.split("'");
  if (parts.length !== 2) {
    return '0';
  }

  const [feetStr, inchesStr] = parts;
  const feet = parseInt(feetStr, 10) || 0;

  if (!inchesStr) {
    return (feet * 12).toString();
  }

  let inches: number;
  switch (inchesStr.trim()) {
    case '1_"':
      inches = 1;
      break;
    case '_"':
      inches = 0;
      break;
    default: {
      const cleanInches = inchesStr.replace('"', '').trim();
      inches = parseInt(cleanInches, 10) || 0;
    }
  }

  const totalInches = feet * 12 + inches;
  return totalInches.toString();
};

export const inchesToFeetInches = (inches: number): string => {
  if (typeof inches !== 'number' || isNaN(inches) || inches < 0) {
    return '0\'0"';
  }

  const feet = Math.floor(inches / 12);
  const remainingInches = Math.floor(inches % 12);

  return `${feet}'${remainingInches}"`;
};

// Generic unit conversion
const convertUnits = (
  rawValue: RawValue,
  from: string,
  to: string,
  unitsMap: Record<UnitsSystem, string>
): RawValue => {
  // Early returns for invalid inputs
  if (!isValidValue(rawValue)) {
    return rawValue;
  }

  if (!from || !to || from === to) {
    return rawValue;
  }

  if (!isValidUnitsSystem(from) || !isValidUnitsSystem(to)) {
    console.warn(`Invalid units system: from="${from}", to="${to}"`);
    return rawValue;
  }

  try {
    const fromUnit = unitsMap[from];
    const toUnit = unitsMap[to];

    if (!fromUnit || !toUnit) {
      console.warn(`Unit mapping not found: from="${fromUnit}", to="${toUnit}"`);
      return rawValue;
    }

    return Qty.swiftConverter(fromUnit, toUnit)(rawValue);
  } catch (error) {
    console.error('Unit conversion failed:', error);
    return rawValue;
  }
};

// Weight conversion
export const weightConvert = (
  rawValue: RawValue,
  from: string,
  to: string
): RawValue => {
  return convertUnits(rawValue, from, to, WEIGHT_UNITS);
};

// Height conversion
export const heightConvert = (
  rawValue: RawValue,
  from: string,
  to: string
): RawValue => {
  return convertUnits(rawValue, from, to, HEIGHT_UNITS);
};

// Utility functions for formatting
export const formatWeight = (
  value: number,
  units: UnitsSystem,
  precision: number = 1
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '-';
  }

  const unit = WEIGHT_UNITS[units];
  return `${value.toFixed(precision)} ${unit}`;
};

export const formatHeight = (
  value: number,
  units: UnitsSystem
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '-';
  }

  if (units === 'imperial') {
    return inchesToFeetInches(value);
  }

  const unit = HEIGHT_UNITS[units];
  return `${Math.round(value)} ${unit}`;
};

// Helper to get unit labels
export const getUnitLabel = (
  type: 'height' | 'weight',
  units: UnitsSystem
): string => {
  const labels = {
    height: units === 'imperial' ? "ft' in\"" : 'cm',
    weight: WEIGHT_UNITS[units],
  };

  return labels[type];
};

// Validation helpers
export const isValidWeight = (value: number, units: UnitsSystem): boolean => {
  if (typeof value !== 'number' || isNaN(value) || value <= 0) {
    return false;
  }

  // Basic range validation
  if (units === 'imperial') {
    return value >= 50 && value <= 1000; // 50-1000 lbs
  } else {
    return value >= 20 && value <= 500; // 20-500 kg
  }
};

export const isValidHeight = (value: number, units: UnitsSystem): boolean => {
  if (typeof value !== 'number' || isNaN(value) || value <= 0) {
    return false;
  }

  // Basic range validation
  if (units === 'imperial') {
    return value >= 24 && value <= 96; // 2-8 feet in inches
  } else {
    return value >= 60 && value <= 250; // 60-250 cm
  }
};

// Legacy exports for backward compatibility
export const unitsOptions = UNITS_OPTIONS;
export const weightUnits = WEIGHT_UNITS;
export const heightUnits = HEIGHT_UNITS;
